import React from 'react';
import { observer } from 'mobx-react';
import SingleButton, { getClassName, getDisabledClassName } from '@/components/SingleButton';
import classnames from 'classnames';
import Msg from '@/components/Msg';
import { getConfig } from '@/config/core';
import { handleRedirectionBtnClick, handleLiveBtnClick } from '@/utils/btnClickHandler';
import EndConfirmModal from '@/components/EndConfirmModal';
import {
  DIALOGUE_FROM, DIALOGUE_TYPE, ButtonType,
  DEFAULT_BUTTON_URL, ButtonActionType, ChasingEventType,
} from '@/types/common';
import { UserType } from '@shopee/shopee-chatbot-agent-chat/dist/types';
import API from '@/services';
import { LIVE_OFFLINE_FORM_PAGE } from '@/utils/constants';
import { formatTransVariable } from '@/utils/formatMessage';
import { modalStore, agentStore } from '@/stores/index';
import { startAgent } from '@/utils/agent';
import { addDialogue } from '@/utils/sessionUtils';
import { tracker } from '@/utils/tracking';
import { getTrans, redirectionTo } from '@/utils/tools';
import systemImage from '@/assets/img/system.svg';
import { PREFIX, IButtons, ShowType, NoticeMsgType, INoticeProps, INoticeState, StartAgentProps } from './types';
import { ChasingButtonClickableStatus, EndChatConfirmType, EndChatButtonId } from '@/utils/tracking/constants';
import trackNotice from './utils/trackNotice';
import './style.scss';

@observer
class Notice extends React.PureComponent<INoticeProps, INoticeState> {
  static defaultProps = {
    // props
    hasHeader: false,
    isHistory: false,
    align: ShowType.Vertical,
    type: NoticeMsgType.Notice,
    passedRef: () => {},
    disabledBtn: false,
    title: '',
    buttons: [],
    impressionTracker: {},
  };

  constructor(props: INoticeProps) {
    super(props);
    this.state = { activeEndModal: false };
  }

  handleOptionClick = (option: IButtons) => () => {
    const { type } = this.props;
    const {
      variables, content, onClick, clickTracker,
      buttonType, url, replyMsg = '', actionType = ButtonActionType.None,
    } = option;
    const { dialogueId = '', queueName, dialogueType, extraParams = {}} = modalStore.startAgentParmas as StartAgentProps;

    switch (buttonType) {
      case ButtonType.Url:
        if (url === DEFAULT_BUTTON_URL) {
          const { webFormConfig } = getConfig();
          const webFormKey = webFormConfig.webFormKey || LIVE_OFFLINE_FORM_PAGE;
          // 跳转webform
          handleRedirectionBtnClick({
            text_value: '',
            text_key: '',
            uri_key: webFormKey,
            isHistory: false,
          });
        } else if (url) {
          redirectionTo(url);
        }
        break;
      case ButtonType.ReplyMsg: {
        const replyContent = content || replyMsg;
        if (replyContent) {
          const msg = {
            from: DIALOGUE_FROM.USER,
            type: DIALOGUE_TYPE.Msg,
            data: { content: formatTransVariable(replyContent, variables), triggeredMsgType: type },
          };
          agentStore.inAgent
            ? agentStore.ADD_AGENT_DATA_ITEM({
              ...msg,
              _useAfterDialogueShow_: true,
            })
            : addDialogue([{
              ...msg,
              dialogueId: dialogueId,
              dialogueType: dialogueType,
            }], false);
        }
        break;
      }
      case ButtonType.ChasingChatWithAgent: {
        // 发送后重新进线
        handleLiveBtnClick({
          dialogueId: '',
          dialogueType: DIALOGUE_TYPE.Live,
          text: getTrans('btn_live'),
          isHistory: false,
        });
        break;
      }
      default:
        break;
    }

    switch (actionType) {
      case ButtonActionType.ContinueOnLine:
        // 发送后继续进线
        startAgent(dialogueId, queueName, dialogueType, { ...extraParams, isSkipPreCheck: true });
        break;
      case ButtonActionType.ChasingChatContinue:
        this.changeChasingStatus(
          ChasingEventType.UserSendYesContinue,
          getTrans('chat_yes_continue'),
        );
        break;
      case ButtonActionType.ChasingEndChat:
        this.changeChasingStatus(
          ChasingEventType.UserSendNoEndChat,
          getTrans('chat_no_end_chat'),
        );
        break;
      default:
        break;
    }

    if (onClick) {
      onClick();
    }

    if (clickTracker) {
      trackNotice(clickTracker);
    }
  };

  generateOption = (option: IButtons, index: number) => {
    const { content = '', variables, disabledText, actionType = ButtonActionType.None } = option;
    const className = disabledText ? getDisabledClassName(option.style) : getClassName(option.style);

    const props = {
      className: classnames('button-container', className),
      key: `button-${index}`,
      text: formatTransVariable(content, variables),
      handleClick: disabledText
        ? () => {}
        : this.getHandleConfirmEndModalFunc(this.handleOptionClick(option), actionType),
      disabled: disabledText,
    };

    return <SingleButton {...props} />;
  };

  changeChasingStatus = (eventType: ChasingEventType, message: string) => {
    (API as { changeChasingStatus: Function }).changeChasingStatus({ // TODO: 统一增加失败重试
      session_id: agentStore.sessionKey,
      event_type: eventType,
      message: { content: message, user_type: UserType.Anonymous },
    });
  };

  getHandleConfirmEndModalFunc = (handleFunc: () => void, actionType?: ButtonActionType) => {
    return actionType === ButtonActionType.ChasingEndChat
      ? () => {
        this.setState({
          activeEndModal: true,
          handleConfirmEndModal: () => {
            handleFunc();
            trackNotice({
              trackingname: 'EndChatConfirm',
              data: { end_type: EndChatConfirmType.ChasingUserEnd, button_id: EndChatButtonId.Continue },
            });
          },
        });
      }
      : handleFunc;
  };

  render() {
    const {
      title, buttons, variables, impressionTracker, hasHeader,
      isHistory, passedRef, disabledBtn, isLastMessage, align,
    } = this.props;
    const { inAgent, loadingInAgent } = agentStore;

    let classNames = PREFIX;
    if (align === ShowType.Horizontal) {
      classNames += ` ${ShowType.Horizontal}`;
    }

    const formatTitle = formatTransVariable(title, variables);
    const formatButtons = buttons.filter((o) => !(o.buttonType === ButtonType.Url && !o.url));
    const formatDisabled = disabledBtn || (
      formatButtons.find(o => o.buttonType === ButtonType.ChasingChatWithAgent)
        ? (inAgent && !loadingInAgent)
        : (!isLastMessage && formatButtons.findIndex(o => o.buttonType !== ButtonType.Url) >= 0)
    );

    const trackingProps = impressionTracker ? {
      'data-trackingname': impressionTracker?.trackingname,
      'data-trackingpage': impressionTracker?.page,
      'data-trackingdata': JSON.stringify({
        ...(impressionTracker?.data || {}),
        clickable_status: formatDisabled ? ChasingButtonClickableStatus.Disabled : ChasingButtonClickableStatus.Active,
      }),
    } : {};

    return (
      <div
        ref={isHistory ? null : passedRef}
        className={classNames}
        {...trackingProps}
      >
        {hasHeader
          && <div className="header">
            <img src={systemImage} />
            <span className="header-text">{`${getTrans('chat_system_reminder')}:`}</span>
          </div>
        }
        <Msg content={formatTitle || getTrans('chat_system_err_default_tip')} />
        <div className="buttons">
          {formatButtons.map((option, index) => {
            return this.generateOption({ ...option, disabledText: formatDisabled }, index);
          })}
        </div>
        <EndConfirmModal
          active={this.state.activeEndModal}
          setActive={(active) => this.setState({ activeEndModal: active })}
          confirmModal={this.state.handleConfirmEndModal}
          closeModal={() => {
            trackNotice({
              trackingname: 'EndChatConfirm',
              data: { end_type: EndChatConfirmType.ChasingUserEnd, button_id: EndChatButtonId.Cancel },
            });
          }}
        />
      </div>
    );
  }
}

export default tracker(Notice);
